import "@/App.css";
import { useEffect, useState, lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { MeshBackground } from "@/components/MeshBackground";
import { Header } from "@/components/Header";
import { Home } from "@/pages/Home";
// Code-split heavy secondary routes — admin + policies load on demand only.
const AdminPanel = lazy(() =>
  import("@/pages/AdminPanel").then((m) => ({ default: m.AdminPanel })),
);
const ReturnPolicy = lazy(() => import("@/pages/ReturnPolicy"));
const PrivacyPolicy = lazy(() => import("@/pages/PrivacyPolicy"));
import { AuthCallbackHandler } from "@/components/AuthCallbackHandler";
import { CustomerAuthDialog } from "@/components/CustomerAuthDialog";
import { MyOrdersDialog } from "@/components/MyOrdersDialog";
import { ReturnOrderDialog } from "@/components/ReturnOrderDialog";
import { ProfileDialog } from "@/components/ProfileDialog";
import { AppInstallBanner } from "@/components/AppInstallBanner";
import { SplashScreen } from "@/components/SplashScreen";
import { ComingSoonScreen } from "@/components/ComingSoonScreen";
import { fetchCustomerMe } from "@/lib/customerAuth";
import { apiGetSiteLive } from "@/lib/api";
import { ADMIN_ROUTE } from "@/lib/constants";

function Shell() {
  const location = useLocation();
  const [cartOpen, setCartOpen] = useState(false);
  const [authOpen, setAuthOpen] = useState(false);
  const [ordersOpen, setOrdersOpen] = useState(false);
  const [returnOpen, setReturnOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);

  // Listen for auto-open triggered right after COD checkout (from Home.jsx).
  useEffect(() => {
    const open = () => setOrdersOpen(true);
    window.addEventListener("mb-open-my-orders", open);
    return () => window.removeEventListener("mb-open-my-orders", open);
  }, []);
  // null = still loading (don't gate yet), true/false = known.
  const [siteLive, setSiteLive] = useState(null);
  const onAdmin = location.pathname === ADMIN_ROUTE;

  // Refresh the cached customer once on boot so an expired token doesn't
  // linger in localStorage between sessions.
  useEffect(() => {
    fetchCustomerMe();
  }, []);

  // Master switch — fetch + listen for in-app toggles dispatched by the admin
  // panel ("mb-maintenance-change"). Re-check when the tab gains focus so a
  // long-idle tab can't view a stale Coming Soon state.
  useEffect(() => {
    let cancelled = false;
    const refresh = async () => {
      try {
        const live = await apiGetSiteLive();
        if (!cancelled) setSiteLive(!!live);
      } catch {
        if (!cancelled) setSiteLive(true); // fail-open to avoid wrongful lockout
      }
    };
    refresh();
    window.addEventListener("mb-maintenance-change", refresh);
    window.addEventListener("focus", refresh);
    return () => {
      cancelled = true;
      window.removeEventListener("mb-maintenance-change", refresh);
      window.removeEventListener("focus", refresh);
    };
  }, []);

  // Master OFF interception. Admin route is ALWAYS excluded so the operator
  // can log in and flip the switch back ON.
  if (siteLive === false && !onAdmin) {
    return <ComingSoonScreen />;
  }

  return (
    <>
      <SplashScreen />
      <AuthCallbackHandler />
      {!onAdmin && <AppInstallBanner />}
      <Header
        onCartClick={onAdmin ? undefined : () => setCartOpen(true)}
        onSignInClick={() => setAuthOpen(true)}
        onMyOrdersClick={() => setOrdersOpen(true)}
        onReturnOrderClick={() => setReturnOpen(true)}
        onProfileClick={() => setProfileOpen(true)}
      />
      <Suspense fallback={null}>
        <Routes>
          <Route
            path="/"
            element={
              <Home
                cartOpen={cartOpen}
                onCartOpen={() => setCartOpen(true)}
                onCartClose={() => setCartOpen(false)}
              />
            }
          />
          <Route path={ADMIN_ROUTE} element={<AdminPanel />} />
          <Route path="/return-policy" element={<ReturnPolicy />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route
            path="*"
            element={
              <Home
                cartOpen={cartOpen}
                onCartOpen={() => setCartOpen(true)}
                onCartClose={() => setCartOpen(false)}
              />
            }
          />
        </Routes>
      </Suspense>
      <CustomerAuthDialog
        open={authOpen}
        onClose={() => setAuthOpen(false)}
        onAuthed={() => setAuthOpen(false)}
      />
      <MyOrdersDialog
        open={ordersOpen}
        onClose={() => setOrdersOpen(false)}
      />
      <ReturnOrderDialog
        open={returnOpen}
        onClose={() => setReturnOpen(false)}
      />
      <ProfileDialog open={profileOpen} onClose={() => setProfileOpen(false)} />
    </>
  );
}

function App() {
  return (
    <div className="App">
      <MeshBackground />
      <BrowserRouter>
        <Shell />
      </BrowserRouter>
      <Toaster
        position="top-center"
        richColors
        closeButton={false}
        toastOptions={{
          style: {
            background: "rgba(2, 44, 34, 0.96)",
            color: "#FFFFFF",
            border: "1px solid rgba(224, 181, 60, 0.45)",
            boxShadow:
              "0 20px 48px rgba(2,44,34,0.35), inset 0 1px 0 rgba(248,228,168,0.18)",
          },
        }}
      />
    </div>
  );
}

export default App;
